import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const SITE_URL = "https://istanova.co";
const DEFAULT_OG = `${SITE_URL}/og-image.jpg`;

export type SEOProps = {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: "website" | "article" | "product";
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  noindex?: boolean;
};

/**
 * Per-page SEO with hreflang for ar/en/tr.
 * `path` should start with "/" and NOT include a language prefix
 * (this app uses a single URL set with client-side language switching).
 */
export const SEO = ({ title, description, path, image, type = "website", jsonLd, noindex }: SEOProps) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "ar").split("-")[0];
  const url = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_OG;
  const jsonLdArr = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang — same URL, different language signal */}
      <link rel="alternate" hrefLang="ar" href={url} />
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="tr" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="ISTANOVA" />
      <meta property="og:locale" content={lang === "ar" ? "ar_AR" : lang === "tr" ? "tr_TR" : "en_US"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdArr.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
};

export const orgJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  name: "ISTANOVA",
  url: SITE_URL,
  logo: `${SITE_URL}/icon-512.png`,
  image: DEFAULT_OG,
  areaServed: { "@type": "City", name: "Istanbul", "@id": "https://www.wikidata.org/wiki/Q406" },
  address: { "@type": "PostalAddress", addressLocality: "Istanbul", addressCountry: "TR" },
});

export { SITE_URL };
